import { UserRole, USER_ROLES } from './roles.js';

/**
 * Permission matrix for role-based access control
 * Defines what each role can view and edit
 */
export interface PermissionMatrix {
  view: 'all' | 'assigned_by_client' | 'read_only_scoped';
  editWorkflowFields: boolean;
  createNotes: boolean;
  manageUsers: boolean;
  manageCampaignSync: boolean;
  manageIncentives: boolean;
  viewClientAchievements: boolean; // View-only client achievements page (no payout data)
  viewTeamPerformance: boolean; // Team Performance page: IM/SDR performance rollups (AM sees own accounts only)
  viewShowRate: boolean; // Show-Rate report page (attended vs no-show per client; IM/SDR/AM scoped to assigned clients by the API)
  manageWebhookIntegrations: boolean; // Create/enable/rotate/delete webhook integrations (page itself is viewable by ADMIN_ASSISTANT)
  editableFields?: string[];
}

/**
 * Permissions for each role
 */
export const PERMISSIONS: Record<UserRole, PermissionMatrix> = {
  [USER_ROLES.ADMIN]: {
    view: 'all',
    editWorkflowFields: true,
    createNotes: true,
    manageUsers: true,
    manageCampaignSync: true,
    manageIncentives: true,
    viewClientAchievements: true,
    viewTeamPerformance: true,
    viewShowRate: true,
    manageWebhookIntegrations: true,
    editableFields: ['im_comment', 'sdr_status', 'sdr_last_contacted_at', 'sdr_comment', 'sdr_owner_user_id', 'priority', 'next_action', 'due_at', 'call_feedback', 'meeting_date', 'meeting_time', 'meeting_timezone'],
  },
  [USER_ROLES.ADMIN_ASSISTANT]: {
    view: 'all', // See all leads (like ADMIN)
    editWorkflowFields: false, // Cannot edit workflow fields
    createNotes: true, // Can create notes (enabled for admin assistants)
    manageUsers: false, // Cannot manage users
    manageCampaignSync: false, // Cannot trigger campaign sync
    manageIncentives: true, // Can access incentives (view/manage)
    viewClientAchievements: true,
    viewTeamPerformance: true,
    viewShowRate: true,
    manageWebhookIntegrations: false, // Read-only on the webhook integrations page
    editableFields: ['call_feedback'], // Can ONLY edit call_feedback (audit logging enabled)
  },
  [USER_ROLES.PM]: {
    view: 'all', // See all leads (like ADMIN)
    editWorkflowFields: true, // Can edit workflow fields
    createNotes: true, // Can create notes
    manageUsers: false, // Cannot manage users
    manageCampaignSync: false, // Cannot trigger campaign sync
    manageIncentives: false, // CANNOT access incentive management features
    viewClientAchievements: true, // CAN view client achievements (read-only, no payout data)
    viewTeamPerformance: true, // PM is admin-family for data scoping — sees all accounts
    viewShowRate: true,
    manageWebhookIntegrations: true, // CAN create/enable/rotate/delete webhook integrations
    editableFields: ['im_comment', 'sdr_status', 'sdr_last_contacted_at', 'sdr_comment', 'sdr_owner_user_id', 'priority', 'next_action', 'due_at', 'call_feedback', 'meeting_date', 'meeting_time', 'meeting_timezone'],
  },
  [USER_ROLES.IM]: {
    view: 'assigned_by_client',
    editWorkflowFields: true,
    createNotes: true,
    manageUsers: false,
    manageCampaignSync: false,
    manageIncentives: false,
    viewClientAchievements: false,
    viewTeamPerformance: false,
    viewShowRate: true,
    manageWebhookIntegrations: false,
    editableFields: ['im_comment', 'priority', 'next_action', 'call_feedback', 'meeting_date', 'meeting_time', 'meeting_timezone'],
  },
  [USER_ROLES.SDR]: {
    view: 'assigned_by_client',
    editWorkflowFields: true,
    createNotes: true,
    manageUsers: false,
    manageCampaignSync: false,
    manageIncentives: false,
    viewClientAchievements: false,
    viewTeamPerformance: false,
    viewShowRate: true,
    manageWebhookIntegrations: false,
    editableFields: ['sdr_status', 'sdr_last_contacted_at', 'sdr_comment', 'next_action', 'due_at', 'call_feedback', 'meeting_date', 'meeting_time', 'meeting_timezone'],
  },
  [USER_ROLES.AM]: {
    view: 'read_only_scoped',
    editWorkflowFields: false,
    createNotes: false,
    manageUsers: false,
    manageCampaignSync: false,
    manageIncentives: false,
    viewClientAchievements: false,
    viewTeamPerformance: true, // AM Team Performance page (scoped to own accounts by the API)
    viewShowRate: true,
    manageWebhookIntegrations: false,
    editableFields: ['meeting_date', 'meeting_time', 'meeting_timezone', 'phone_override', 'to_name_override', 'company_name_override', 'website_override'],
  },
};

/**
 * Check if a role can edit a specific field
 * 
 * @param role - User role
 * @param fieldName - Field name to check
 * @returns true if role can edit the field
 */
export function canEditField(role: UserRole, fieldName: string): boolean {
  const permissions = PERMISSIONS[role];
  return permissions.editableFields?.includes(fieldName) ?? false;
}

/**
 * Check if a role has a specific permission
 * 
 * @param role - User role
 * @param permission - Permission key to check
 * @returns true if role has the permission
 */
export function hasPermission<K extends keyof PermissionMatrix>(
  role: UserRole,
  permission: K
): PermissionMatrix[K] {
  return PERMISSIONS[role][permission];
}

/**
 * Check if a role can view client achievements (no payout data)
 *
 * @param role - User role
 * @returns true if role can view client achievements
 */
export function canViewClientAchievements(role: UserRole): boolean {
  return PERMISSIONS[role]?.viewClientAchievements ?? false;
}

/**
 * Check if a role can view Team Performance (IM/SDR performance rollups).
 * AMs are scoped to their own assigned accounts by the API.
 *
 * @param role - User role
 * @returns true if role can view Team Performance
 */
export function canViewTeamPerformance(role: UserRole): boolean {
  return PERMISSIONS[role]?.viewTeamPerformance ?? false;
}

/**
 * Check if a role can view the Show-Rate report page.
 * IM/SDR/AM see only their assigned clients (enforced by the API).
 *
 * @param role - User role
 * @returns true if role can view the Show-Rate report
 */
export function canViewShowRate(role: UserRole): boolean {
  return PERMISSIONS[role]?.viewShowRate ?? false;
}